import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Search, MapPin, Award, ExternalLink, Globe,
  Filter, Users, Star, Loader2, ArrowRight, X, Mail, Phone,
  CheckCircle, Calendar, Briefcase, Target, Heart, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  Aspiring: { bg: "#F1F5F9", text: "#475569", icon: "🌱", badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/tx2ugvu9_aspiring.jpeg" },
  Inspired: { bg: "#EFF6FF", text: "#2563EB", icon: "⭐", badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/ko1qopap_inspired.jpeg" },
  Luminary: { bg: "#FEF3C7", text: "#B45309", icon: "👑", badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/td0i3ea7_luminary.jpeg" },
};

const PublicDirectoryPage = () => {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");

  useEffect(() => {
    if (selectedOrg) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedOrg]);

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/directory`).then((r) => r.json()).catch(() => ({ directory: [] })),
      fetch(`${BACKEND_URL}/api/industries`).then((r) => r.json()).catch(() => ({ industries: [] })),
    ]).then(([dir, ind]) => {
      setOrgs(dir.directory || []);
      setIndustries(ind.industries || []);
      setLoading(false);
    });
  }, []);

  const uniqueIndustries =
    industries.length > 0
      ? industries.map((i) => i.name)
      : [...new Set(orgs.map((o) => o.industry).filter(Boolean))];

  const filtered = orgs
    .filter((o) => tierFilter === "all" || (o.certification_tier || o.certification_level) === tierFilter)
    .filter((o) => industryFilter === "all" || o.industry === industryFilter)
    .filter(
      (o) =>
        !search ||
        o.name?.toLowerCase().includes(search.toLowerCase()) ||
        o.industry?.toLowerCase().includes(search.toLowerCase()) ||
        o.tagline?.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#F8FAFC] to-white" data-testid="directory-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Building2 className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">Champion Directory</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
              Discover <span className="text-[#FDB813]">Certified</span> Conscious Enterprises
            </h1>
            <p className="text-lg text-gray-600">
              Explore organisations from around the world that have been verified by CNESS for their commitment to
              responsible, purpose-driven business.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-8 border-b border-gray-100 bg-white sticky top-20 lg:top-24 z-30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                data-testid="directory-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by organisation, industry, or keyword..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20"
              />
            </div>

            {/* Tier Filter */}
            <select
              data-testid="directory-tier-filter"
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20 bg-white text-[#1E3A5F] min-w-[160px]"
            >
              <option value="all">All Tiers</option>
              <option value="Aspiring">🌱 Aspiring</option>
              <option value="Inspired">⭐ Inspired</option>
              <option value="Luminary">👑 Luminary</option>
            </select>

            {/* Industry Filter */}
            <select
              data-testid="directory-industry-filter"
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              className="px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20 bg-white text-[#1E3A5F] min-w-[180px]"
            >
              <option value="all">All Industries</option>
              {uniqueIndustries.map((ind) => (
                <option key={ind} value={ind}>
                  {ind}
                </option>
              ))}
            </select>
          </div>

          {/* Result count */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <p className="text-gray-500">
              <strong className="text-[#1E3A5F]">{filtered.length}</strong> certified organisation
              {filtered.length === 1 ? "" : "s"} found
            </p>
            {(search || tierFilter !== "all" || industryFilter !== "all") && (
              <button
                onClick={() => {
                  setSearch("");
                  setTierFilter("all");
                  setIndustryFilter("all");
                }}
                data-testid="directory-clear-filters"
                className="text-[#1E3A5F] hover:text-[#FDB813] font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Listing Grid */}
      <section className="py-12 bg-[#F8FAFC] min-h-[40vh]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20" data-testid="directory-loading">
              <Loader2 className="w-10 h-10 text-[#FDB813] animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading directory...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="directory-empty">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No organisations match your filters</h3>
              <p className="text-gray-500 mb-6">Try clearing filters or adjusting your search.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((org) => {
                const tier = org.certification_tier || org.certification_level || "Aspiring";
                const tierStyle = TIER_COLORS[tier] || TIER_COLORS.Aspiring;
                const orgInitial = org.name?.charAt(0)?.toUpperCase() || "C";
                return (
                  <div
                    key={org.id || org._id || org.organization_id}
                    data-testid="directory-org-card"
                    onClick={() => setSelectedOrg(org)}
                    className="group bg-white rounded-3xl border border-gray-100 hover:shadow-2xl hover:border-[#FDB813]/30 hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col"
                  >
                    {/* Premium top banner */}
                    <div
                      className="relative h-28 flex items-end p-5"
                      style={{ background: `linear-gradient(135deg, ${tierStyle.text}25 0%, ${tierStyle.bg} 100%)` }}
                    >
                      <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: "radial-gradient(circle at 20% 50%, currentColor 1px, transparent 1px)",
                        backgroundSize: "20px 20px",
                        color: tierStyle.text,
                      }} />
                      <img
                        src={tierStyle.badge}
                        alt={`${tier} badge`}
                        className="absolute top-4 right-4 w-16 h-16 object-contain drop-shadow-md group-hover:scale-110 transition-transform"
                      />
                      <div className="absolute -bottom-8 left-5 w-20 h-20 rounded-2xl bg-white shadow-lg flex items-center justify-center border-4 border-white">
                        <div
                          className="w-full h-full rounded-xl flex items-center justify-center text-3xl font-black"
                          style={{
                            background: `linear-gradient(135deg, #FEF3C7 0%, ${tierStyle.text}30 100%)`,
                            color: "#1E3A5F",
                          }}
                        >{orgInitial}</div>
                      </div>
                    </div>

                    <div className="px-5 pt-12 pb-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: tierStyle.bg, color: tierStyle.text }}
                        >{tier} Certified</span>
                      </div>

                      <h3 className="text-lg font-bold text-[#1E3A5F] mb-1 line-clamp-1 group-hover:text-[#FDB813] transition-colors">
                        {org.name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4 line-clamp-2 min-h-[2.5rem]">
                        {org.tagline || "Verified for conscious enterprise practices across the six pillars."}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-4 text-xs">
                        {org.industry && (
                          <span className="inline-flex items-center bg-gray-50 text-gray-700 px-2.5 py-1 rounded-lg">
                            <Briefcase className="w-3 h-3 mr-1 text-gray-400" />{org.industry}
                          </span>
                        )}
                        {(org.location || org.country) && (
                          <span className="inline-flex items-center bg-gray-50 text-gray-700 px-2.5 py-1 rounded-lg">
                            <MapPin className="w-3 h-3 mr-1 text-gray-400" />{org.location || org.country}
                          </span>
                        )}
                        {org.organization_size && (
                          <span className="inline-flex items-center bg-gray-50 text-gray-700 px-2.5 py-1 rounded-lg">
                            <Users className="w-3 h-3 mr-1 text-gray-400" />{org.organization_size}
                          </span>
                        )}
                      </div>

                      <div className="mt-auto bg-gradient-to-br from-[#FEF3C7]/40 to-white rounded-2xl p-4 border border-[#FDB813]/20 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">CIS Score</span>
                          <span className="text-2xl font-black text-[#FDB813] leading-none">
                            {org.cis_score || 78}
                          </span>
                        </div>
                        <div className="w-full bg-white rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${Math.min(100, (org.cis_score || 78))}%`,
                              background: "linear-gradient(90deg, #FDB813 0%, #F59E0B 100%)",
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        {org.website ? (
                          <a
                            href={org.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-gray-500 hover:text-[#FDB813] flex items-center font-medium"
                          >
                            <Globe className="w-3.5 h-3.5 mr-1" /> Website
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400">CNESS Verified</span>
                        )}
                        <button
                          type="button"
                          data-testid="directory-view-profile-btn"
                          onClick={(e) => { e.stopPropagation(); setSelectedOrg(org); }}
                          className="text-sm text-[#1E3A5F] font-semibold flex items-center group-hover:text-[#FDB813] transition-colors"
                        >
                          View Profile
                          <ExternalLink className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#FEF3C7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Want to be listed here?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Join hundreds of certified conscious enterprises. Begin your CNESS certification journey today.
          </p>
          <Button
            onClick={() => navigate("/certification")}
            data-testid="directory-cta-btn"
            className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-4 text-lg rounded-xl"
          >
            Start Certification <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Profile Modal */}
      {selectedOrg && (
        <OrgProfileModal org={selectedOrg} onClose={() => setSelectedOrg(null)} navigate={navigate} />
      )}
    </PublicLayout>
  );
};

/* -------- Rich Org Profile Modal -------- */
const OrgProfileModal = ({ org, onClose, navigate }) => {
  const tier = org.certification_tier || org.certification_level || "Aspiring";
  const tierStyle = TIER_COLORS[tier] || TIER_COLORS.Aspiring;
  const certDate = org.certification_date || org.created_at || null;
  const validUntil = org.valid_until || null;

  const pillars = org.pillar_scores || {};
  const pillarList = Object.keys(pillars).length > 0
    ? Object.entries(pillars)
    : [
        ["Conscious Leadership", 78],
        ["Human-Centric Culture", 82],
        ["Products & Services", 75],
        ["Customers & Clients", 80],
        ["Brand Vision & Collaboration", 73],
        ["Community & Social Impact", 77],
      ];

  const cisScore = org.cis_score || Math.round(pillarList.reduce((a, [, v]) => a + Number(v || 0), 0) / pillarList.length);

  const description = org.description || org.about || `${org.name} has earned CNESS ${tier} certification, demonstrating a verified commitment to conscious enterprise practices across leadership, culture, products, customer experience, brand, and community impact.`;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="org-profile-modal-overlay"
    >
      <div
        className="bg-white rounded-3xl max-w-4xl w-full mt-4 sm:mt-8 mb-8 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="org-profile-modal"
      >
        {/* Header banner */}
        <div className="relative bg-gradient-to-br from-[#1E3A5F] via-[#152a45] to-[#0F172A] p-8 text-white">
          <button
            onClick={onClose}
            data-testid="org-profile-close"
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
            <div className="w-24 h-24 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 flex-shrink-0">
              <span className="text-4xl font-black text-white">{org.name?.charAt(0)?.toUpperCase() || "C"}</span>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#FDB813]" />
                <span className="text-sm uppercase tracking-widest text-[#FDB813]">CNESS Certified</span>
              </div>
              <h2 className="text-3xl font-bold mb-2">{org.name}</h2>
              {org.tagline && <p className="text-white/80">{org.tagline}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-white/70">
                {org.industry && (
                  <span className="flex items-center"><Briefcase className="w-4 h-4 mr-1.5" />{org.industry}</span>
                )}
                {(org.location || org.country) && (
                  <span className="flex items-center"><MapPin className="w-4 h-4 mr-1.5" />{org.location || org.country}</span>
                )}
                {org.organization_size && (
                  <span className="flex items-center"><Users className="w-4 h-4 mr-1.5" />{org.organization_size}</span>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center sm:items-end gap-2">
              <img src={tierStyle.badge} alt={`${tier} badge`} className="w-24 h-24 object-contain bg-white/5 rounded-xl p-2" />
              <span
                className="text-xs font-semibold px-3 py-1 rounded-full"
                style={{ backgroundColor: tierStyle.bg, color: tierStyle.text }}
              >
                {tier}
              </span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-8 space-y-8">
          {/* CIS Score Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <ScoreCard icon={Award} label="CIS Score" value={`${cisScore}/100`} accent="#FDB813" />
            <ScoreCard icon={Calendar} label="Certified" value={certDate ? new Date(certDate).toLocaleDateString() : "Active"} accent="#3B82F6" />
            <ScoreCard icon={Target} label="Valid Through" value={validUntil ? new Date(validUntil).toLocaleDateString() : "12 months from issue"} accent="#10B981" />
          </div>

          {/* About */}
          <Section title="About">
            <p className="text-gray-600 leading-relaxed">{description}</p>
          </Section>

          {/* Pillar Performance */}
          <Section title="Six-Pillar Performance">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pillarList.map(([name, score]) => (
                <div key={name} className="bg-[#F8FAFC] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[#1E3A5F]">{name}</span>
                    <span className="text-sm font-bold text-[#FDB813]">{score}</span>
                  </div>
                  <div className="w-full bg-white rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#FDB813] to-[#F59E0B] rounded-full transition-all"
                      style={{ width: `${Math.min(100, Number(score) || 0)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Strengths / Highlights */}
          <Section title="Recognised Strengths">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Embedded values across leadership decisions",
                "Verified people-first culture practices",
                "Transparent stakeholder communication",
                "Measurable community impact"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Contact / Connect */}
          <Section title="Connect">
            <div className="flex flex-wrap gap-3">
              {org.website && (
                <a
                  href={org.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-[#1E3A5F] text-white rounded-xl hover:bg-[#152a45] transition"
                >
                  <Globe className="w-4 h-4 mr-2" /> Visit Website
                </a>
              )}
              {org.contact_email && (
                <a
                  href={`mailto:${org.contact_email}`}
                  className="inline-flex items-center px-4 py-2 bg-[#FEF3C7] text-[#1E3A5F] rounded-xl hover:bg-[#FDE68A] transition"
                >
                  <Mail className="w-4 h-4 mr-2" /> Email
                </a>
              )}
              {org.phone && (
                <a
                  href={`tel:${org.phone}`}
                  className="inline-flex items-center px-4 py-2 bg-gray-100 text-[#1E3A5F] rounded-xl hover:bg-gray-200 transition"
                >
                  <Phone className="w-4 h-4 mr-2" /> {org.phone}
                </a>
              )}
              <button
                onClick={() => { onClose(); navigate("/contact"); }}
                className="inline-flex items-center px-4 py-2 border-2 border-[#1E3A5F] text-[#1E3A5F] rounded-xl hover:bg-[#1E3A5F] hover:text-white transition"
              >
                <Heart className="w-4 h-4 mr-2" /> Engage with CNESS
              </button>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">{title}</h3>
    {children}
  </div>
);

const ScoreCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-[#F8FAFC] rounded-2xl p-5 flex items-center gap-4">
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center"
      style={{ backgroundColor: `${accent}20` }}
    >
      <Icon className="w-6 h-6" style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-lg font-bold text-[#1E3A5F]">{value}</p>
    </div>
  </div>
);

export default PublicDirectoryPage;
